import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Pencil } from "lucide-react";

import { CircularProgress } from "@/components/ui/circular-progress";
import { MuiCheckIcon } from "@/components/ui/mui-field-icons";
import {
  MUI_FIELD_ERROR,
  MUI_FIELD_TRIGGER,
  MUI_MENU_ITEM,
  MUI_MENU_ITEM_SELECTED,
} from "@/components/ui/mui-field-shape";
import { RippleButton } from "@/components/ui/ripple-button";
import {
  MenuSelect,
  MenuSelectContent,
  MenuSelectNotice,
  MenuSelectSearch,
  MenuSelectTrigger,
} from "@/components/ui/menu-select";
import { MuiAlert } from "@/components/ui/mui-alert";
import { MuiDeleteOutlineOutlinedIcon } from "@/components/ui/mui-spec-icons";
import { Separator } from "@/components/ui/separator";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { LookupOption } from "@/lib/catalog-admin";
import { BffUserFacingError, errorMessageForDisplay } from "@/lib/errors";
import { cn } from "@/lib/utils";

// The donor's `CustomSelectWithPagination`: the control behind Manufacturer,
// Color/Finish and Material on every ported catalog form.
//
// What it observably does, and what this carries over —
//   - a search box pinned inside the menu, narrowing the list as you type;
//   - an "Add <label>" row pinned to the bottom that opens an inline field and
//     selects whatever comes back;
//   - a pencil on each row (when the field is editable) that renames in place;
//   - **no control at all on a locked row** — ADR-0005 §13's Unspecified
//     Manufacturer, which the catalog structurally depends on. Not a disabled
//     one: a disabled button still says this is something you could be allowed
//     to do.
//
// **Infinite scroll is carried** (#412). The donor pages its lookup query 100 at
// a time and appends the next page when the last rendered row scrolls into view
// (`CustomSelectWithPagination.tsx:32,113-127`); so does this control, given the
// four optional props below — `useCatalogLookupMenu` supplies them. Left off,
// the control falls back to narrowing a caller-supplied `options` list locally,
// which is what the one genuinely small list still does
// (`add-variant-dialog.tsx:330`).
//
// **Delete-by-clearing-the-name is carried, behind a confirmation.** Submitting
// an inline rename empty deletes the lookup (`CustomSelectWithPagination.tsx`:
// `if (name.trim() === '')` → clear the field if it held that row, delete, drop
// it from the list, close the menu). In the donor that is an unlabelled,
// unconfirmed destructive gesture; #256 gives delete a named control behind a
// confirmation on the Admin lookup panes, so this control was porting a worse
// door onto the same act. Raised in the decision round as D3, and the owner
// ruled: **keep the gesture, put a question in front of it.** The gesture is
// therefore the donor's — an emptied name is still what asks — and the
// confirmation is the one recorded parity break on this control. Everything
// after the answer is the donor's sequence, step for step.

export type { LookupOption } from "@/lib/catalog-admin";

/** `useDebounce(filter, 200)` — `CustomSelectWithPagination.tsx:43`, and named
 * in #253. Exported so the tests advance exactly this clock. */
export const LOOKUP_SEARCH_DEBOUNCE_MS = 200;

export interface LookupSelectFieldProps {
  label: string;
  options: readonly LookupOption[];
  value: number | null;
  onChange: (next: number | null) => void;
  required?: boolean;
  disabled?: boolean;
  /** The options are still on their way — the donor's `isFetching`, which
   * draws a spinner *under* whatever rows it already has rather than replacing
   * them (`CustomSelectWithPagination.tsx:247-257`). */
  loading?: boolean;
  error?: string;
  /**
   * The options read failed.
   *
   * Distinct from an empty list, and the distinction is the point: "No
   * matches." tells a typist to try another word, which is the wrong
   * instruction when the read never arrived.
   */
  failed?: boolean;
  /** The donor's `isEditable`: whether rows offer an inline rename. */
  editable?: boolean;
  /** MUI's `size` — `small` where the donor passes it (the per-plan
   *  manufacturer dialog); the catalog's forms take the 56px default. */
  size?: "small" | "medium";
  /**
   * What the current value is called, when the option list cannot say.
   *
   * **The donor's trigger never waits on its lookup query.** Its form value *is*
   * the option object — `formDefaultData` seeds it straight off the item
   * (`useEditProduct.ts:31,37,38`) and the select renders
   * `renderValue={selected => JSON.parse(selected).name}`
   * (`CustomSelectWithPagination.tsx:203-204`), so the name is on screen from
   * first paint and the query only fills the *menu*. Resolving the display out
   * of the option list instead left three required fields blank for the length
   * of three reads on every cold load, permanently if a read failed, and
   * forever for an item pointing at a row that is no longer alive.
   */
  unresolvedLabel?: string;
  /**
   * Another page of options exists behind the ones shown — the donor's
   * `data?.hasNextPage`.
   */
  hasMore?: boolean;
  /**
   * Fetch that next page. Absent means this menu does not page at all, and no
   * observer is attached: the one call site whose list is a single inherited
   * row (`add-variant-dialog.tsx`) has nothing to scroll for.
   */
  onLoadMore?: () => void;
  /**
   * The search term, 200ms after the typing stopped — for whoever owns the read
   * that decides which rows arrive.
   *
   * **The donor filters in two layers**, and both are needed: the term goes to
   * the server as `name__icontains` (`CustomSelectWithPagination.tsx:52`), and
   * the rows that come back are filtered again locally (`:100-111`). The second
   * layer is not redundant — the first page of a 2,000-row list is never all in
   * hand — and the local pass is what makes a keystroke feel instant over rows
   * already held.
   */
  onSearchSettled?: (term: string) => void;
  /** Absent means the menu offers no add row at all. */
  onCreateOption?: (name: string) => Promise<LookupOption>;
  /** Absent means no row offers a rename, whatever `editable` says. */
  onRenameOption?: (id: number, name: string) => Promise<LookupOption>;
  /** The donor's delete-by-clearing-the-name. Absent means an empty rename is
   * refused instead. */
  onDeleteOption?: (id: number) => Promise<void>;
  /**
   * An explicit delete control on each unlocked row, beside its pencil — the
   * per-plan manufacturer dialog's named break (#767 item 4, #858), where a
   * delete is an act of its own and never an emptied rename. Absent means no
   * row draws one, which is every catalog form.
   *
   * The control reports the press: what a delete asks first (a usage read,
   * then a confirm or a refusal) is the caller's, so this owns no confirm.
   * While the returned promise is pending the control's place holds a spinner
   * and a second press goes nowhere.
   *
   * **The menu must be shut when the caller's next UI renders**: the menu is
   * modal, so a dialog opened under it sits behind it and is hidden from
   * assistive technology. So the caller gets `close` with the option and
   * calls it in the same synchronous block that opens its UI — one commit,
   * the menu gone and the dialog up. Once the promise fulfils the menu closes
   * regardless, for a caller that opens nothing; a rejection leaves it as it
   * is.
   */
  onRequestDelete?: (option: LookupOption, menu: { close: () => void }) => Promise<void> | void;
  className?: string;
  "data-testid"?: string;
}

/** The row being renamed, or the add row, and the text in its field. Only ever
 * one at a time — which is what keeps the menu from becoming a form. */
type Draft = { kind: "create" } | { kind: "rename"; option: LookupOption };

/**
 * What a refused write says.
 *
 * The donor reads `err?.message || err?.data?.error || '<its own sentence>'`
 * (`AddOptionButton.tsx:27`, `EditableOption.tsx:47`). Atlas's analogue of
 * `data.error` is a tagged `BffUserFacingError` — anything else has been
 * scrubbed to a generic 500 by the middleware long before it reaches here, and
 * the donor's own sentence says more to a typist than that does.
 */
const draftErrorMessage = (kind: Draft["kind"], cause: unknown) =>
  cause instanceof BffUserFacingError
    ? errorMessageForDisplay(cause)
    : kind === "create"
      ? "Failed to add option. Please try again."
      : "Failed to edit option. Please try again.";

export function LookupSelectField({
  label,
  options,
  value,
  onChange,
  required = false,
  disabled = false,
  loading = false,
  error,
  failed = false,
  hasMore = false,
  onLoadMore,
  onSearchSettled,
  editable = false,
  size,
  unresolvedLabel,
  onCreateOption,
  onRenameOption,
  onDeleteOption,
  onRequestDelete,
  className,
  "data-testid": testId,
}: LookupSelectFieldProps) {
  const triggerId = useId();
  const labelId = useId();
  const messageId = useId();
  const draftId = useId();

  const [open, setOpen] = useState(false);
  /**
   * **The box's text and the filter in force are two things, as they are in the
   * donor.**
   *
   * There, `filter` is component state and the box is an *uncontrolled*
   * `TextField` (`search-input.tsx:27-43`) that remounts with the menu. Typing
   * moves both together, so the pair are indistinguishable — until a write
   * closes the menu, which leaves `filter` standing and blanks the box by
   * remounting it (see `closeAfterWrite`). One state cannot be in two places at
   * once, so this is two.
   */
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const searched = (next: string) => {
    setSearch(next);
    setTerm(next);
  };

  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);
  /**
   * The emptied rename is waiting on an answer.
   *
   * Its own flag rather than a third `Draft` kind, because the draft it is
   * asking about has not changed: cancelling puts the typist back in the same
   * rename field, with the row's name one keystroke away.
   */
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  /**
   * Bumped on every close.
   *
   * A lookup write is awaited, and the dialog around this control can close
   * while it is in flight — Escape, the overlay, Cancel. The response then
   * arrives to a form that has already been reset and would take the new id as
   * its manufacturer, so reopening showed a blank draft with one field
   * mysteriously filled. Snapshotting the count before the await and comparing
   * after is how a late answer knows it is answering a question nobody is still
   * asking.
   */
  const generation = useRef(0);

  /**
   * **There is no optimistic overlay, because the write itself moves the
   * menu.**
   *
   * There was one: a map of rows this control had just written, laid over
   * `options` until a refetch agreed with it. It is gone, and both halves of
   * the reasoning that put it there were wrong.
   *
   * It waited for a refetch that never comes. The donor's three lookup
   * mutations invalidate a tag nothing provides — the list provides
   * `{type: "load-manufacturers"}` and the writes invalidate
   * `{type: "create-manufacturer"}` and siblings (`itemsApi.ts:416-419`) — so
   * no write has ever re-read that list. Its menu moves by local patch alone:
   * `setCurrentOptions([...currentOptions, newOption])` (`:169`), mapped in
   * place on rename (`:146-152`), filtered out on delete (`:137-140`). Atlas
   * matches that in the mutations, where it belongs (`catalog-admin.ts`,
   * "A write invalidates the unpaged read and *patches* the paged one") — so by
   * the time `await onCreateOption(...)` returns, `options` already holds the
   * row, in the place the donor puts it. An overlay would be a second, private
   * copy of a patch the cache has already applied for every reader of that
   * query.
   *
   * And it was never what kept the trigger named. That is `picked`'s job,
   * below, keyed on the value rather than on the list, so no window can take it
   * away. #412's parity review, rounds 3-5, finding 1.
   */

  /**
   * The row this control last handed up, kept so the trigger never depends on
   * which window the menu happens to be holding.
   *
   * **The donor has no equivalent because it does not need one**: its form
   * value *is* the option object and `renderValue` reads `.name` straight off
   * it (`CustomSelectWithPagination.tsx:203-205`), so the lookup query only
   * ever fills the menu. Atlas's form value is an id, which was fine while
   * `options` was the whole alive list and stopped being fine the moment it
   * became one page of one search term: pick a row found by typing, and 200ms
   * later the settled term re-keys the read, the cache serves page 1, and the
   * id no longer resolves. The field went **blank** on the two dialogs and
   * reverted to the item's *saved* name on the edit page — while the draft
   * held the new id. #412's parity review, finding 1.
   *
   * Not `unresolvedLabel`: that is the caller's answer for a value it was
   * given, which on the edit page is the saved row — the wrong name here.
   * Matched on id, so a value the parent changes underneath us drops it.
   */
  const [picked, setPicked] = useState<LookupOption | null>(null);

  /** The row whose delete control the caller is still answering. */
  const [requestingDelete, setRequestingDelete] = useState<number | null>(null);
  /** The same, synchronously: two presses inside one task both read the
   *  state before either render, so the state alone cannot refuse the second. */
  const requestInFlight = useRef(false);
  const requestDelete = async (option: LookupOption) => {
    if (!onRequestDelete || requestInFlight.current) return;
    requestInFlight.current = true;
    setRequestingDelete(option.id);
    const era = generation.current;
    // Only the menu this press was made in — a reopened one is another.
    const close = () => {
      if (generation.current === era) dismiss();
    };
    try {
      await onRequestDelete(option, { close });
      close();
    } catch {
      // The caller's to report; the menu stays as it is.
    } finally {
      requestInFlight.current = false;
      setRequestingDelete(null);
    }
  };

  const selected =
    options.find((option) => option.id === value) ?? (picked?.id === value ? picked : null);
  /** The trigger's text: the resolved row, else the caller's own answer for the
   * value it handed us — see `unresolvedLabel`. */
  const display = selected?.name ?? (value === null ? "" : (unresolvedLabel ?? ""));

  // 200ms behind the box, as the donor's is — see `useDebouncedValue`.
  const debouncedTerm = useDebouncedValue(term, LOOKUP_SEARCH_DEBOUNCE_MS);

  /**
   * Layer one: the term the read is keyed on, reported to whoever owns it.
   *
   * An effect rather than a call in the change handler, because the value being
   * reported is the *debounced* one — it becomes true 200ms after the event
   * that caused it, when there is no handler left to be inside. The *term*,
   * not the box: a write-closed menu keeps its term, and re-reading it would be
   * the whole point of `closeAfterWrite` undone.
   *
   * Through a ref, and the ref is the load-bearing part: the event this fires
   * on is *the term settling*, so the term is the only dependency. Listing the
   * callback too made a caller who passes a fresh lambda each render — the
   * ordinary way to write one — re-fire on every render, and if that lambda
   * sets state, the pair spin until the tab dies. It did: this file's own
   * windowed-list harness hung the browser runner rather than failing.
   */
  const reportSettled = useRef(onSearchSettled);
  useEffect(() => {
    reportSettled.current = onSearchSettled;
  });
  useEffect(() => {
    reportSettled.current?.(debouncedTerm);
  }, [debouncedTerm]);

  /**
   * Layer two: the local pass over the rows already held.
   *
   * **Narrowing waits for the debounce; widening does not.** The donor's
   * `handleFilterChange` special-cases the empty value and restores the full
   * list synchronously (`:182-184`), so deleting a term brings the rows back on
   * that keystroke while the *query* still waits out its 200ms. Read off the
   * live term for that one case and the debounced value otherwise, which is
   * that special case as a condition rather than a second copy of the state.
   *
   * The *term*, never the box: after a write-closed menu reopens, the box is
   * blank and the list is still narrowed, which is the donor's own quirk.
   */
  // Neither value is trimmed, and the widen test is `=== ""` exactly, both as
  // the donor has them (`:182`, `:104`): "matte " keeps only "Matte Black"
  // there, and trimming kept "Matte" too — #412's parity review, finding 3.
  const needle = term === "" ? "" : debouncedTerm.toLowerCase();
  const shown = needle
    ? options.filter((option) => option.name.toLowerCase().includes(needle))
    : options;

  /**
   * Menu closed, and every draft it was holding dropped. The donor's drafts
   * live in components that unmount with the menu, so both exits below lose
   * them; a half-typed new name should not be waiting the next time the menu
   * opens either way.
   */
  const shut = () => {
    generation.current += 1;
    setOpen(false);
    setSearch("");
    setDraft(null);
    setDraftName("");
    setDraftError(null);
    setConfirmingDelete(false);
  };

  /** The editor gone and nothing else — the donor's `setEditing(false)`
   * (`EditableOption.tsx:45`). The menu, its rows and its term stay put. */
  const leaveDraft = () => {
    setDraft(null);
    setDraftName("");
    setDraftError(null);
    setConfirmingDelete(false);
  };

  /** The typist dismissed the menu — Escape, a click outside, a row picked.
   * MUI `Select`'s `onClose`, and the only place the donor clears its filter
   * (`CustomSelectWithPagination.tsx:206-210`). */
  const dismiss = () => {
    shut();
    setTerm("");
  };

  /** A write closed the menu. The donor gets here by setting its own controlled
   * `open` prop false (`:141`, `:155`, `:171`), which does not run `onClose` —
   * so the term stays in force and the box goes blank on its own. Keeping the
   * term is also what keeps the write's cache patch: the term is in the paged
   * query's key, and clearing it re-reads page 1 in sort order over the top of
   * the row just written. Round 6, finding 2. */
  const closeAfterWrite = shut;

  /**
   * The rename form, for the click that abandons it.
   *
   * `EditableOption.tsx:19-33` listens for a document `mousedown` and, when it
   * lands outside its own form, drops the editing state and its error — so
   * clicking the search box, another row, or the add row cancels a rename in
   * the donor rather than leaving two live drafts in one menu. The listener is
   * the rename's alone: `AddOptionButton` has none, and its draft survives
   * until it is submitted or the menu closes.
   */
  const renameFormRef = useRef<HTMLDivElement>(null);

  /**
   * The donor's infinite scroll: watch the last rendered row, and when it comes
   * into view fetch the page behind it
   * (`CustomSelectWithPagination.tsx:72-88`).
   *
   * A **callback ref** rather than the `useEffect`-on-a-stable-ref shape
   * `catalog-batch-toolbar.tsx:73-80` uses, because the node under observation
   * changes every time a page is appended — the effect would have to depend on
   * the row count to keep up, which is the callback ref written longhand.
   *
   * `new IntersectionObserver(cb)` with **no options object**, as the donor
   * constructs it. The defaults — root the viewport, `rootMargin: "0px"`,
   * `threshold: 0` — are exactly the toolbar's explicit `INTERSECTION_OPTIONS`,
   * so this differs from that constant in spelling only, and matching the donor
   * literally is the rule where they agree.
   *
   * `loading` bails before observing, not inside the callback: that is the
   * donor's `if (isFetching) return` at `:75`, and it is what stops scrolling
   * on through a loading list from queueing a second page on top of the one
   * already coming.
   */
  const observer = useRef<IntersectionObserver | null>(null);

  /**
   * **One ask per arrival at the foot while the pager is failing** — the
   * bound on retries, and the one place this observer is not the donor's.
   *
   * `observe()` reports the current state immediately, and the observer is
   * rebuilt every time `isFetching` settles, so a last row still in view asks
   * again the instant a refusal lands: measured at 42 requests a second
   * against a stubbed server. The donor storms in exactly that shape — it
   * destructures no error at all (`CustomSelectWithPagination.tsx:50`) and
   * its `data?.hasNextPage` stays true off the last good read.
   *
   * Refusing to ask again *at all* was the first fix and went too far the
   * other way: it retired the pager for that term, which is a user-visible
   * loss the donor does not have and no ruling covers (#412, cross-review run
   * 2). This keeps the donor's recovery and drops only its rate — after a
   * refusal the foot must be *re-entered* before it asks again, so a scroll
   * away and back retries, and a page that succeeds re-arms below.
   */
  const askable = useRef(true);
  useEffect(() => {
    if (!failed) askable.current = true;
  }, [failed]);

  const lastOptionRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      observer.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        // Leaving the foot re-arms: this is what makes a scroll back down a
        // fresh ask rather than a continuation of the refused one.
        if (!entry.isIntersecting) {
          askable.current = true;
          return;
        }
        if (!askable.current || !hasMore) return;
        if (failed) askable.current = false;
        onLoadMore?.();
      });
      if (node) observer.current.observe(node);
    },
    [loading, failed, hasMore, onLoadMore],
  );

  // The donor leaks its observer; a control that mounts three times per dialog
  // should not.
  useEffect(() => () => observer.current?.disconnect(), []);

  useEffect(() => {
    if (draft?.kind !== "rename") return;
    const onMouseDown = (event: MouseEvent) => {
      if (renameFormRef.current?.contains(event.target as Node)) return;
      leaveDraft();
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [draft]);

  const startDraft = (next: Draft) => {
    setDraft(next);
    setDraftName(next.kind === "rename" ? next.option.name : "");
    setDraftError(null);
    setConfirmingDelete(false);
  };

  const submitDraft = async () => {
    /**
     * **Trimmed only to answer "is this blank?".** That is the one place the
     * donor trims: `AddOptionButton.tsx:74` disables the confirm while the name
     * is blank, and `CustomSelectWithPagination.tsx:132` tests
     * `name.trim() === ''` for the delete gesture. What it *sends* is the raw
     * value (`AddOptionButton.tsx:23`, `EditableOption.tsx:43`), so " Brass "
     * is a real write there. Atlas's server trims it on arrival under ADR-0005,
     * which is a licensed catalog-wide rule — but the write still happens, and
     * trimming here had quietly cancelled it. Round 6, finding 3.
     */
    const name = draftName.trim();
    if (!draft || saving) return;
    // **An empty add is unsubmittable, not refused in words.**
    // `AddOptionButton.tsx:74` disables the confirm button while the name is
    // blank — which also blocks the form's implicit submission — so the donor
    // never says anything about it. Enter reaches here anyway, and bails the
    // same way the disabled button does.
    if (!name && !(draft.kind === "rename" && onDeleteOption)) return;
    // The one place this control leaves the donor, and it leaves it on the
    // owner's ruling rather than on its own judgement (decision round, D3): the
    // gesture stays, the deletion waits for an answer. Nothing has happened
    // yet — no field cleared, no row dropped — so cancelling costs the typist
    // only the keystroke that emptied the name.
    if (!name && draft.kind === "rename" && !confirmingDelete) {
      setDraftError(null);
      setConfirmingDelete(true);
      return;
    }
    // Everything past here awaits. If the control closes meanwhile — the dialog
    // around it can be dismissed mid-flight — the answer is stale and must not
    // be written back into a draft that has since been reset.
    const era = generation.current;
    const stale = () => generation.current !== era;
    /**
     * What to put back if the write is refused.
     *
     * Only the delete branch sets it, and only when the row being deleted is
     * the one the field holds. The clear still happens first — that is the
     * donor's order and what a user sees — but the donor never undoes it: it
     * does not `.unwrap()` the mutation (`CustomSelectWithPagination.tsx:132-136`),
     * so a refused delete there leaves the item pointing at nothing, silently,
     * and the next Save writes that emptiness down. Restoring on refusal is the
     * owner's ruling (decision round, D2).
     */
    let restoreOnRefusal: number | null = null;

    setSaving(true);
    setDraftError(null);
    try {
      if (draft.kind === "create") {
        const created = await onCreateOption!(draftName);
        if (stale()) return;
        // Selected on the way out, as the donor does: the reason to add one
        // from inside a select is to use it.
        //
        // Held in `picked` as well, because the patched cache entry is the
        // shorter-lived of the two — `gcTime: 0` drops it the moment the menu
        // closes, and the next read sorts the row wherever the server puts it,
        // possibly outside the window or outside the term.
        setPicked(created);
        onChange(created.id);
        closeAfterWrite();
      } else if (!name) {
        // The donor's delete gesture, step for step: clear the field if it held
        // this row, delete, drop it from the list, close the menu.
        if (value === draft.option.id) {
          restoreOnRefusal = draft.option.id;
          onChange(null);
        }
        await onDeleteOption!(draft.option.id);
        if (stale()) return;
        // Nothing to hide the row behind: the delete filters it out of the
        // cached pages before this resolves, so the window on screen has
        // already lost it.
        closeAfterWrite();
      } else if (draftName === draft.option.name) {
        // **An unchanged name is not a write, and not a close.**
        // `EditableOption.tsx:41-45` guards the call on `name !== option.name`
        // and then `setEditing(false)` — so no request goes out, no audit stamp
        // lands on a row that did not move, and the parent's `setOpen(false)`
        // (the only thing that closes this menu) never runs. The typist who
        // thought better of a rename is left in the menu they were in, rows and
        // term intact. Round 6, finding 1.
        //
        // Compared raw, as the donor compares it: "Kohler " over "Kohler" is a
        // real rename there, and the trimmed comparison made it a silent
        // no-op here. Round 6, finding 3.
        leaveDraft();
      } else {
        const renamed = await onRenameOption!(draft.option.id, draftName);
        if (stale()) return;
        // The held row follows the rename — otherwise the trigger keeps the
        // name the row was *picked* under and reverts to it the moment the
        // overlay retires and the window moves on. Whether this control's own
        // value is the renamed row or not: if it is not, the row it holds is
        // simply left alone.
        setPicked((current) =>
          value === draft.option.id || current?.id === renamed.id ? renamed : current,
        );
        if (value === draft.option.id) onChange(renamed.id);
        // **The menu closes on a successful rename**, as the donor's does:
        // `CustomSelectWithPagination.tsx:144-155` patches the list, re-points
        // the field, then `setOpen(false)`. Leaving it open left the typist in
        // a menu they had finished with. The term it was filtered by outlives
        // the close — see `closeAfterWrite`.
        closeAfterWrite();
      }
    } catch (cause) {
      if (stale()) return;
      if (restoreOnRefusal !== null) onChange(restoreOnRefusal);
      // The menu stays open on a refusal — the name is still in the field to be
      // fixed rather than retyped.
      setDraftError(draftErrorMessage(draft.kind, cause));
    } finally {
      setSaving(false);
    }
  };

  /** The donor's inline refusal: `@modsy/react-ui`'s closable error `Alert`,
   * not a bare line of red text. */
  const draftAlert = draftError ? (
    <MuiAlert severity="error" onClose={() => setDraftError(null)}>
      {draftError}
    </MuiAlert>
  ) : null;

  /**
   * The question in front of the donor's delete gesture (D3).
   *
   * It replaces the name field rather than sitting under it: leaving an empty
   * input on screen beside a Delete button invites a second submit, which is
   * the very keystroke the confirmation exists to catch.
   */
  const deleteConfirmation =
    draft?.kind === "rename" ? (
      <div className="flex flex-col gap-2 px-3 py-2">
        <p className="text-lennar-ink text-sm">Delete {draft.option.name}?</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => void submitDraft()}
            className="text-destructive hover:bg-lennar-action-hover rounded-[4px] px-2 py-1 text-sm font-medium disabled:opacity-50"
          >
            Delete
          </button>
          <button
            type="button"
            disabled={saving}
            // Back to the rename field, not out of the menu — the draft itself
            // was never touched.
            onClick={() => setConfirmingDelete(false)}
            className="text-lennar-ink hover:bg-lennar-action-hover rounded-[4px] px-2 py-1 text-sm disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
        {draftAlert}
      </div>
    ) : null;

  const draftField =
    confirmingDelete && deleteConfirmation ? (
      deleteConfirmation
    ) : (
      <div className="flex flex-col p-[12.8px]">
        {/* Above the field when adding (`AddOptionButton.tsx:43-52`, `mb: 1.6`)
            and below it when renaming (`EditableOption.tsx:94-105`, flush) —
            the donor puts the same alert on two different sides. */}
        {draft?.kind === "create" ? <div className="mb-[12.8px]">{draftAlert}</div> : null}
        {/* The donor's shape exactly: a `position: relative` `Box` holding a
            full-width `TextField size='small'` with the confirm `IconButton`
            laid *over* its right end at `right: 4`, rather than beside it
            (`AddOptionButton.tsx:53-83`, `EditableOption.tsx:63-93`). */}
        <div className="relative flex w-full items-center">
          <input
            id={draftId}
            // `AddOptionButton.tsx:65` and `EditableOption.tsx:75` both carry
            // it: pressing "Add Manufacturer" or a row's pencil puts the caret
            // in the field and you type. Without it the removed row left focus
            // on `document.body` and the gesture needed a second click.
            autoFocus
            type="text"
            aria-label={
              draft?.kind === "rename" ? `Rename ${draft.option.name}` : `New ${label} name`
            }
            value={draftName}
            // **The refusal has to be on the element, not just in the class.**
            // `MUI_FIELD_ERROR` only wins because `MUI_FIELD_BASE` stands its
            // focus, hover and open rules down under `aria-invalid`
            // (`mui-field-shape.ts`) — without the attribute the base's
            // `focus:not-aria-invalid:` carries one more pseudo-class than the
            // override and a refused name goes blue under the caret. It is
            // also simply true of this input, and what a screen reader needs:
            // the donor's inline `TextField` carries MUI's `error`
            // (`AddOptionButton.tsx:71`, `EditableOption.tsx:82`), which sets
            // the same attribute.
            aria-invalid={draftError ? true : undefined}
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={(event) => {
              // Enter submits, because the menu is not a form: a nested `<form>`
              // inside the dialog's own would submit the dialog.
              if (event.key === "Enter") {
                event.preventDefault();
                void submitDraft();
              }
              // **Escape does nothing here.** Both donor inline forms swallow
              // every key before it leaves the field (`AddOptionButton.tsx:67`,
              // `EditableOption.tsx:77`), so Escape while typing a name neither
              // abandons the draft nor closes the menu. The way out is the
              // click outside that the rename listens for.
              event.stopPropagation();
            }}
            className={cn(
              MUI_FIELD_TRIGGER,
              // **No room reserved for the tick.** The donor's `TextField` keeps
              // MUI's own 14px on the right and lets the absolutely placed
              // `IconButton` sit over its end, so a long name runs under the
              // tick (`AddOptionButton.tsx:61-82`). A `pr-12` here put the text
              // 49px from the edge (#576's parity review).
              "w-full",
              // MUI's `error` on the inline `TextField` (`AddOptionButton.tsx:71`,
              // `EditableOption.tsx:82`): the outline goes red and stays red
              // while the refused name is being fixed.
              draftError && MUI_FIELD_ERROR,
            )}
          />
          <button
            type="button"
            aria-label={`Save ${label}`}
            // Empty is not submittable **on the add row**, and only there: the
            // donor's two rows disagree and both are carried. `AddOptionButton`
            // disables its confirm on `name.trim() === ''`
            // (`AddOptionButton.tsx:74`), so a nameless option cannot be asked
            // for; `EditableOption`'s confirm carries no `disabled` at all and
            // its handler sends anything that differs from the original
            // (`EditableOption.tsx:39-48`, `:82-92`), so modsy posts the empty
            // rename and lets the server refuse it.
            //
            // Keeping the rename row live also keeps #252's owner-ruled delete
            // gesture reachable, where emptying the name *is* the delete.
            //
            // **Not disabled while saving, either.** Neither donor confirm
            // greys for the write — `AddOptionButton.tsx:74`'s `disabled` is
            // the blank check alone and `EditableOption`'s has none — and
            // Atlas's did for the whole save (#576's parity review). A second
            // press sends nothing: `submitDraft` refuses re-entry while one is
            // in flight.
            disabled={draft?.kind === "create" && !draftName.trim()}
            onClick={() => void submitDraft()}
            className={cn(
              // MUI's `IconButton` at its default size — 8px of padding round a
              // 24px glyph, 40px square — at the donor's `right: 4`
              // (`AddOptionButton.tsx:76-79`), which lands the glyph 12px from
              // the field's edge. `ButtonBase`'s `cursor: pointer`
              // (`ButtonBase.js:63`), which a bare `<button>` does not have.
              "hover:bg-lennar-action-hover absolute right-1 cursor-pointer rounded-full p-2",
              // MUI's disabled `IconButton`: `action.disabled` ink with no
              // opacity of its own (`IconButton.js:142-145`), and the
              // `ButtonBase`'s `pointer-events: none` (`ButtonBase.js:76-77`),
              // so no hover tint on a tick that cannot be pressed. It was
              // `opacity-50` over the resting ink.
              "disabled:text-lennar-action-disabled disabled:pointer-events-none",
              // **The two forms' ticks are not the same colour.** The rename's
              // `IconButton` carries `color: 'primary.main'`
              // (`EditableOption.tsx:85-89`); the add's carries only
              // `position`/`right` (`AddOptionButton.tsx:76-79`), so it takes
              // MUI's `IconButton` default, `palette.action.active`.
              draft?.kind === "rename" ? "text-lennar-blue-600" : "text-lennar-action-active",
            )}
          >
            <MuiCheckIcon />
          </button>
        </div>
        {draft?.kind === "rename" ? draftAlert : null}
      </div>
    );

  return (
    <MenuSelect
      open={open}
      onOpenChange={(next) => {
        if (next) setOpen(true);
        else dismiss();
      }}
      trigger={
        <MenuSelectTrigger
          label={label}
          display={display}
          open={open}
          required={required}
          disabled={disabled}
          error={error}
          triggerId={triggerId}
          labelId={labelId}
          messageId={messageId}
          size={size}
          className={className}
          data-testid={testId}
        />
      }
    >
      <MenuSelectContent role="listbox" aria-label={label}>
        <MenuSelectSearch value={search} onChange={searched} />

        {/* ┌── PARITY BREAK ─────────────────────────────────────────────────┐
            **The donor's menu says nothing in words.** A list that came back
            empty — filtered to nothing, or failed — gets the search box and the
            add row with a gap between them
            (`CustomSelectWithPagination.tsx:231-257`), so each of the three
            lines below is a sentence a modsy user never sees.

            **The owner ruled to keep them**: #254's decision round, D15 = a
            (2026-09-02) — they are strictly more helpful than the donor, so
            they stay, recorded as named parity breaks rather than removed.
            (They *were* removed, by #415; #412 restores them, which is one of
            its acceptance criteria.)
            └─────────────────────────────────────────────────────────────────┘

            **Where the break stops.** "Loading…" answers an *empty* menu only.
            A menu that already holds rows is appending a page, and #407 ported
            the donor's mark for that: the centred spinner at the foot, below.
            The two are not alternatives — the first read draws both — but a
            line of copy arriving at the foot on every appended page would
            shift the rows the pointer is resting on, which the donor never
            does and D15 did not ask for. */}
        {loading && shown.length === 0 ? <MenuSelectNotice>Loading…</MenuSelectNotice> : null}
        {/* The retry is the newer fact: "could not be loaded" under a live
            spinner would contradict it. And the same `shown.length === 0` bound
            "Loading…" carries, for the same reason: a *failed append* leaves a
            hundred rows on screen, and telling their reader the list could not
            be loaded contradicts the list in front of them. The donor shows
            nothing at all there — it destructures only `{data, isFetching,
            refetch}` (`CustomSelectWithPagination.tsx:50`). Round 2, finding 3. */}
        {!loading && failed && shown.length === 0 ? (
          <MenuSelectNotice>
            {label} could not be loaded. Close the dialog and try again.
          </MenuSelectNotice>
        ) : null}
        {!loading && !failed && shown.length === 0 ? (
          <MenuSelectNotice>No matches.</MenuSelectNotice>
        ) : null}
        {shown.map((option, index) => {
          const renaming = draft?.kind === "rename" && draft.option.id === option.id;
          // The donor's `ref={index === filteredOptions.length - 1 ? ... }`
          // (`:236`) — the *filtered* list's last row, so a narrowed menu pages
          // on its own last row rather than the unfiltered list's.
          const watched = index === shown.length - 1 && onLoadMore !== undefined;
          const deletable = onRequestDelete !== undefined && !option.isLocked;
          const pencilled = editable && onRenameOption !== undefined && !option.isLocked;
          if (renaming)
            return (
              // **Watched while it is being renamed, too.** The donor's ref is
              // on the `MenuItem` wrapper, which renders in both states —
              // `EditableOption` swaps only its own contents (`:232-244`,
              // `EditableOption.tsx:51,110`). Skipping it here detached paging
              // whenever the pencil was open on the bottom row: modsy appends
              // the next hundred, and Atlas appended nothing until the rename
              // was abandoned. #412's parity review, round 3, finding 3.
              <div
                key={option.id}
                ref={(node) => {
                  renameFormRef.current = node;
                  if (watched) lastOptionRef(node);
                }}
              >
                {draftField}
              </div>
            );
          return (
            <div
              key={option.id}
              ref={watched ? lastOptionRef : null}
              className="group/option flex items-center"
            >
              {/* No `focusRipple`: these rows are `MenuItem`s, which never set
                  it, so Space picks the option without inking. */}
              <RippleButton
                role="option"
                aria-selected={option.id === value}
                onClick={() => {
                  setPicked(option);
                  onChange(option.id);
                  dismiss();
                }}
                className={cn(
                  MUI_MENU_ITEM,
                  "flex-1 gap-2 py-1.5",
                  option.id === value && MUI_MENU_ITEM_SELECTED,
                )}
              >
                <span className="truncate">{option.name}</span>
              </RippleButton>
              {/* An Atlas addition (#858), so there is no donor control to
                  match: the pencil's twin — the same 24px box, navy ink, and
                  hidden until the row is hovered or focused — with the shared
                  icon set's outlined delete glyph, which the editor already
                  draws. **Before the pencil**, so the pencil keeps the donor's
                  place at the row's end. The spinner replaces it while the
                  caller works, at rest as well as under the pointer. */}
              {deletable ? (
                requestingDelete === option.id ? (
                  <span
                    className={cn(
                      "flex size-6 shrink-0 items-center justify-center",
                      pencilled ? "mr-2" : "mr-4",
                    )}
                  >
                    <CircularProgress size={20} />
                  </span>
                ) : (
                  <button
                    type="button"
                    aria-label={`Delete ${option.name}`}
                    onClick={() => void requestDelete(option)}
                    className={cn(
                      "text-lennar-blue-600 hidden shrink-0 cursor-pointer group-focus-within/option:block group-hover/option:block",
                      // The row's 16px right inset belongs to the last control.
                      pencilled ? "mr-2" : "mr-4",
                    )}
                  >
                    <MuiDeleteOutlineOutlinedIcon />
                  </button>
                )
              ) : null}
              {pencilled ? (
                <button
                  type="button"
                  aria-label={`Rename ${option.name}`}
                  onClick={() => startDraft({ kind: "rename", option })}
                  // **`display: none` until the row is hovered**, not
                  // `opacity-0` — the donor's is a bare 24px `EditIcon` with
                  // `display: 'none'` and `&:hover .edit-option-button {
                  // display: block }` on the row (`EditableOption.tsx:110-133`),
                  // so it takes *no width at all* at rest and the name has the
                  // whole row. Laid out permanently and merely faded, it made
                  // all three menus about 36px wider than the source's, and
                  // `w-max` panels show that as extra width the whole time.
                  // No ripple and no hover ground either: the donor's is an
                  // `SvgIcon`, not an `IconButton`.
                  //
                  // **Focus reveals it as hover does** — the owner's ruling
                  // (decision round, D6). The donor's cannot be reached without
                  // a mouse at all: `display: none` keeps it out of the tab
                  // order, so a keyboard user has no rename. Revealing it while
                  // the row it belongs to holds focus puts it back in reach and
                  // changes nothing at rest, which is what the `display: none`
                  // was protecting — a pencil laid out permanently made all
                  // three menus about 36px wider at every moment.
                  className="text-lennar-blue-600 mr-4 hidden shrink-0 group-focus-within/option:block group-hover/option:block"
                >
                  <Pencil aria-hidden="true" className="size-6" />
                </button>
              ) : null}
            </div>
          );
        })}

        {/* **Under the options, not over them** — the donor renders the
            spinner after `filteredOptions.map` and before the sticky add row
            (`CustomSelectWithPagination.tsx:229-257`), so a menu that already
            holds options keeps showing them while the next read runs and the
            spinner marks the foot. Drawn above the list instead, it pushed
            every option down a row each time the read re-ran.

            And it is `isFetching`, not "no data yet": the donor re-reads on
            scroll (`:75-87`) and on a settled filter (`:52-54`, `:97-98`) — not
            after a write, which never re-reads at all (round 5) — and shows
            this spinner on both. Call sites pass the same flag. */}
        {loading ? (
          <div className="flex justify-center py-2.5">
            <CircularProgress size={24} />
          </div>
        ) : null}

        {onCreateOption ? (
          <div className="bg-popover sticky bottom-0">
            {/* MUI's `Divider` is `palette.divider`, `rgba(0,0,0,0.12)`, which
                the donor palette does not override (`CustomSelectWithPagination
                .tsx:266`). The vendored `Separator`'s own `bg-border` is an
                opaque grey from the shadcn palette, so this line and the
                category menu's — which already used the token — were two
                different colours on one screen. */}
            <Separator className="bg-lennar-divider" />
            {draft?.kind === "create" ? (
              draftField
            ) : (
              // A `Button variant='text'` with `sx={{ p: 1.6, color: '#000000',
              // justifyContent: 'flex-start' }}` (`AddOptionButton.tsx:88-110`):
              // **pure black**, not the 87% ink every other row wears, over a
              // text button's hover ground — `alpha(primary.main, 0.04)`, the
              // navy tint rather than the neutral `action.hover`. Its label is a
              // `<Typography>`, so it is `body1` at 16px and not the 14px
              // `typography.button` the `Button` root would have given it.
              <RippleButton
                // A `Button` donor, so `focusRipple` — unlike the option rows
                // above it, which are `MenuItem`s and stay silent under Space
                // (`Button.js:579` vs. zero `focusRipple` in `MenuItem.js`).
                focusRipple
                onClick={() => startDraft({ kind: "create" })}
                className="hover:bg-lennar-primary-hover flex w-full items-center p-[12.8px] text-left text-base text-black"
              >
                {/* `sx={{ mr: 0.5, color: 'lightTextSecondary.main' }}` — 4px,
                    and the theme's own `rgba(0, 0, 0, 0.6)` (`theme.ts:74-77`).

                    Material's `Add`, inlined: `M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z`
                    (`@mui/icons-material/Add.js:12`) is a **filled 14×14
                    glyph** — x and y both run 5 to 19 — inside the same 24px
                    box MUI's `SvgIcon` gives it at its default `fontSize`. The
                    icon set's `Plus` strokes two 2px bars across nearly the
                    whole box, ~16×16 of ink, which read as a heavier plus
                    beside the donor's on the same row: measured in #412's
                    pixel review, ruled in its decision round (D8 = a,
                    2026-09-15). Inlined the way `icon-menu-button.tsx` inlines
                    `more_vert` and `chip-list.tsx` inlines `cancel`, because
                    the shape is the source's. The box stays 24px, so nothing
                    around it moves. */}
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="text-lennar-ink-secondary mr-1 size-6 fill-current"
                >
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
                </svg>
                Add {label}
              </RippleButton>
            )}
          </div>
        ) : null}
      </MenuSelectContent>
    </MenuSelect>
  );
}
